import React, { Fragment, useState } from 'react';
import * as _ from 'lodash';
import { Formik, Field, FieldArray } from 'formik';
import { throwError, EMPTY } from 'rxjs';
import { catchError, pluck } from 'rxjs/operators';
import { ajax } from 'rxjs/ajax';
import cn from 'classnames';
import { convertToEditable, convertToDb, stubVendorSetting, stubCertificate } from './convert';
import { TdAjax } from '../lib/tdAjax';
import toastr from '../toastr';
import './saml-config.scss';

const CERT_MIME_TYPE = 'application/x-x509-ca-cert';
const fileIsCert = file => file.type === CERT_MIME_TYPE;

const notify = msg => toastr.info(msg);
const notifyError = msg => toastr.error(_.truncate(msg, { length: 300 }), null, {
  timeOut: 0,
  extendedTimeOut: 0,
  closeButton: true,
  tapToDismiss: false
});

const catchIf = (cond, f) => (err, caught$) => {
  if (_.isMatch(err, cond)) {
    return f(err, caught$);
  }
  return throwError(err);
};

const catchUnauthorized = catchIf({ status: 401 }, _e => {
  window.location = '/signin';
  return EMPTY;
});

const resErrorMessages = res => _.chain(res)
  .get('response.errors')
  .values()
  .flatten()
  .map(v => v.message ? v.message : v)
  .value();

const saveSamlConfig = samlConfig =>
  TdAjax.put(`/saml_configs/${samlConfig.id}`, { saml_config: samlConfig }).pipe(
    pluck('response'),
    catchError(catchUnauthorized),
    catchError(catchIf({ status: 422 }, res => throwError(_.merge(res, {
      errorMessages: resErrorMessages(res)
    })))));

const createSamlConfig = samlConfig =>
  TdAjax.post('/saml_configs', { saml_config: samlConfig }).pipe(
    pluck('response'),
    catchError(catchUnauthorized),
    // TODO: on create, response.errors is [String]. but on update,
    // response.errors is [{message:String].... why??
    catchError(catchIf({ status: 422 }, res => throwError(_.merge(res, {
      errorMessages: resErrorMessages(res)
    })))));

const handleCertInfoError = res => {
  console.error('cert info error', res);
  let errorMsg = res.message;
  if (_.isMatch(res, { status: 422 })) {
    errorMsg = _.get(res, 'response.errors', []).join(', ');
  }
  notifyError(errorMsg);
};

const processCert = file => {
  const data = new FormData();
  data.append('certificate', file);

  return ajax({
    url: '/saml_configs/cert_info',
    method: 'POST',
    headers: {
      'x-csrf-token': document.querySelector('meta[name=csrf-token]').getAttribute('content')
    },
    body: data
  }).pipe(pluck('response'));
};

const samlSettingTypeCodes = [
  ['Attribute', 'SAMLTYPE_ATTRIBUTE'],
  ['Attribute Mapping', 'SAMLTYPE_ATTRIBUTEMAPPING'],
  ['Static', 'SAMLTYPE_VENDOR']
];

const samlConfigFields = [
  { name: 'issuer', label: 'Issuer', type: 'text' },
  { name: 'allowable_clock_drift_min', label: 'Allowable Clock Drift (minutes)', type: 'text' },
  { name: 'dob_format', label: 'DOB Format', type: 'text' },
];

const SamlConfigFields = ({ samlConfig }) => _.map(
  samlConfigFields,
  ({ name, label, type }) => (
    <div className="form-row flex items-center mb-1" key={`samlconfigfield_${name}`}>
      <div className="font-bold mr-3 w-1/6 text-right">
        <label
          className="font-bold text-right"
          htmlFor={name}
        >
          {label}
        </label>
      </div>
      <div className="w-5/6">
        <Field
          name={name}
          type={type}
          className={cn(`block form-${type}`, { 'w-1/2': type === 'text' })}
          {...(type === 'checkbox' ? { checked: samlConfig[name] } : {})}
        />
      </div>
    </div>
  )
);

const certColumns = [
  'Sha1 Fingerprint',
  'Expiration',
  'Actions'
];
const certMoreInfo = cert => ({
  'Serial (int)': (<span className="font-mono">{cert.serial_int}</span>),
  'Serial (hex)': (<span className="font-mono">{cert.serial_hex}</span>),
  Subject: cert.subject,
  Issuer: cert.issuer
});
const SamlCertificates = () => {
  let fileInput;
  const [active, setActive] = useState(null);
  const toggleActive = i => {
    if (active !== i) {
      setActive(i);
    } else if (active === i) {
      setActive(null);
    }
  };
  return (
    <div className="mt-3">
      <h3 className="bg-adminblue-800 text-white font-bold py-1 px-2">Signing Certificates</h3>
      <FieldArray
        name="certificates"
        render={({ form: { values:samlConfig }, ...arrayHelpers }) => (
          <Fragment>
            <table className="table-auto">
              <thead>
                <tr>
                  {certColumns.map((v, i) => <th key={`certcol${i}`}>{v}</th>)}
                </tr>
              </thead>
              <tbody>
                {samlConfig.certificates.map((cert, i) => (
                  <Fragment key={`cert${i}`}>
                    <tr className={cn({ 'bg-adminblue-100': cert.isNew })}>
                      <td>
                        <Field
                          type="text"
                          name={`certificates.${i}.sha1_fingerprint`}
                          className="form-text w-full"
                        />
                      </td>
                      <td className="align-middle">{cert.expiry_dt}</td>
                      <td className="text-right" width="25%">
                        <button
                          type="button"
                          className="button button-light button-small mr-2"
                          onClick={() => toggleActive(i)}
                        >
                          {active === i ? 'Close' : 'More Info'}
                        </button>
                        <button
                          type="button"
                          className="button button-light button-small"
                          onClick={() => arrayHelpers.remove(i)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                    {active === i &&
                      <tr>
                        <td colSpan={certColumns.length} className="px-5 pt-1 pb-5">
                          <div className="flex flex-row">
                            <div className="flex-grow">
                              {_.map(certMoreInfo(cert), (val, key) => (
                                <p key={`moreinfo${i}${key}`}>
                                  <span className="font-bold">{key}:</span> {val}
                                </p>
                              ))}
                            </div>
                            <div className="ml-3">
                              <p className="font-bold">PEM</p>
                              <p className="whitespace-pre">{cert.public_certificate_pem}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    }
                  </Fragment>
                ))}
              </tbody>
            </table>
            <div className="text-right mt-2">
              <input
                type="file"
                name="file"
                className="button button-light button-small button-upload"
                accept={`.cer,.crt,.pem,.der,${CERT_MIME_TYPE}`}
                ref={ref => fileInput = ref}
                onChange={e => {
                  const file = e.target.files[0];
                  processCert(file).subscribe({
                    next: cert => {
                      fileInput.value = '';
                      arrayHelpers.push(_.merge(cert, { isNew: true }));
                    },
                    errors: handleCertInfoError
                  });
                }}
              />
              <button
                type="button"
                className="button button-light button-small"
                onClick={() => { arrayHelpers.push(_.merge(stubCertificate(), { isNew: true })); }}
              >
                New
              </button>
            </div>
          </Fragment>
        )}
      />
    </div>
  );
};

const SamlVendorSettings = ({ samlConfig, samlNameCodes }) => (
  <div className="mt-3">
    <h3 className="bg-adminblue-800 text-white font-bold py-1 px-2">Vendor Settings</h3>
    <FieldArray
      name="vendor_settings"
      render={arrayHelpers => (
        <div>
          <table>
            <thead>
              <tr>
                <th>Setting Type</th>
                <th>Setting Name Code</th>
                <th>Setting Value</th>
                <th>Source Attribute Name</th>
                <th>Source Attribute Value</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="">
              {samlConfig.vendor_settings.map((vendorSetting, index) => (
                <tr
                  key={`vendorSetting${index}`}
                  className={cn({ 'bg-adminblue-100': vendorSetting.isNew })}
                >
                  <td>
                    <Field
                      name={`vendor_settings.${index}.setting_type_code`}
                      component="select"
                      className="form-select"
                    >
                      {samlSettingTypeCodes.map(([label, value], i) => (
                        <option value={value} key={`samltypecode${i}`}>{label}</option>
                      ))}
                    </Field>
                  </td>
                  <td>
                    <Field
                      name={`vendor_settings.${index}.setting_name_code`}
                      component="select"
                      className="form-select"
                    >
                      {samlNameCodes.map(([label, value], i) => (
                        <option value={value} key={`samlnamecode${i}`}>{label}</option>
                      ))}
                    </Field>
                  </td>
                  <td>
                    <Field
                      name={`vendor_settings.${index}.setting_value`}
                      type="text"
                      className="form-text"
                    />
                  </td>
                  <td>
                    <Field
                      name={`vendor_settings.${index}.source_attribute_name`}
                      type="text"
                      className="form-text"
                    />
                  </td>
                  <td>
                    <Field
                      name={`vendor_settings.${index}.source_attribute_value`}
                      type="text"
                      className="form-text"
                    />
                  </td>
                  <td className="text-right">
                    <button
                      type="button"
                      className="button button-light button-small"
                      onClick={() => arrayHelpers.remove(index)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-right mt-2">
            <button
              type="button"
              className="button button-light button-small"
              onClick={() => arrayHelpers.push(_.merge({ isNew: true }, stubVendorSetting()))}
            >
              Add Vendor Setting
            </button>
          </div>
        </div>
      )}
    />
  </div>
);

const EditSamlConfig = props => {
  const [samlConfig, setSamlConfig] = useState(props.samlConfig);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [draggedFileIsCert, setDraggedFileIsCert] = useState(false);

  const handleDragEnter = e => {
    const file = e.dataTransfer.items[0];
    e.preventDefault();
    setIsDragOver(true);
    setDraggedFileIsCert(fileIsCert(file));
  };
  const handleDragOver = e => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDrageLeave = _e => {
    setIsDragOver(false);
  };

  const update = config => {
    setIsSubmitting(true);
    saveSamlConfig(config)
      .subscribe({
        next: newSamlConfig => {
          setIsSubmitting(false);
          setSamlConfig(newSamlConfig);
          notify('Saml config saved');
        },
        error: e => {
          console.error('save saml config error', e);
          setIsSubmitting(false);
          notifyError(`Error saving saml config: ${e.errorMessages ? e.errorMessages.join(', ') : e.message}`);
        }
      });
  };
  const create = config => {
    setIsSubmitting(true);
    createSamlConfig(config)
      .subscribe({
        next: newSamlConfig => {
          notify('Created. Navigating to edit...');
          window.location =  `/saml_configs/${newSamlConfig.id}/edit`;
        },
        error: e => {
          console.error('create saml config error', e);
          setIsSubmitting(false);
          notifyError(`Error creating saml config: ${e.errorMessages ? e.errorMessages.join(', ') : e.message}`);
        }
      });
  };
  const save = props.isNew ? create : update;

  return (
    <Formik
      enableReinitialize
      initialValues={convertToEditable(samlConfig)}
      onSubmit={values => {
        save(convertToDb(values));
      }}
      render={({ values, handleSubmit, setValues }) => (
        <div
          className="relative use-box-border"
          onDrop={e => {
            e.preventDefault();
            setIsDragOver(false);
            const file = e.dataTransfer.files[0];
            if (fileIsCert(file)) {
              processCert(file).subscribe({
                next: cert => {
                  setValues(_.merge(values, {
                    certificates: values.certificates.slice().concat([
                      _.merge({ isNew: true }, cert)
                    ])
                  }));
                },
                error: handleCertInfoError
              });
            }
          }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDrageLeave}
        >
          <div
            className={cn(
              'absolute w-full h-full bg-whitetrans-800 p-6 text-center pointer-events-none',
              { hidden: !isDragOver }
            )}
          >
            <p className="text-6xl font-bold pt-5 text-gray-700 pointer-events-none">
              {draggedFileIsCert ? '✅' : '❌'}
              <br />
              {draggedFileIsCert ? 'Drop here' : 'File type not recognized'}
            </p>
          </div>

          <div className="flex items-center h2-border">
            <h2 className="text-xl text-blue-800 font-bold pt-3 pb-2 flex-grow overflow-x-hidden">
              {props.isNew && "New Saml Config"}
              {!props.isNew && `Edit SAML Config for ${samlConfig.issuer}`}
            </h2>
            {!props.isNew &&
              <div className="flex-grow-0">
                <a className="button button-primary mt-3" href={`/saml_configs/${samlConfig.id}/test`}>
                  Test
                </a>
              </div>
            }
          </div>

          <div className="">
            <form onSubmit={handleSubmit}>
              <SamlConfigFields samlConfig={values} />
              <SamlCertificates />
              <SamlVendorSettings samlConfig={values} samlNameCodes={props.samlNameCodes} />
              <div className="mt-1">
                <input
                  type="submit"
                  value="Save"
                  className="button button-primary"
                  disabled={isSubmitting}
                />
                {isSubmitting && <em className="ml-4">Saving...</em>}
              </div>
            </form>
          </div>
        </div>
      )}
    />
  );
};
export { EditSamlConfig };
